import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as Editsignature } from '../../assets/images/editsignature.svg';
import { AlertColor, Grid, Tooltip } from '@mui/material';

import { useEffect, useState, useContext } from 'react';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import SignatureComponent from '../../components/formlib/modal/SignatureComponent';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { SignatureStateType } from '../../utils/type';
import { isOtherLanguage } from '../../TranslateWidget';
import { LookUpContext } from '../../context/LookUpContextProvider';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import { ConsentClientSignature } from '../../services/configApi/forms/Consent/ConsentServices';

type ConsentSignatureType = {
  signatureData: ConsentClientSignature[];
  relationshipWithChildId?: string;
  defaultRelationshipWithChildId?: string;
  emailId?: string;
  isDateRequired?: boolean;
  isVerbalConsentRequired?: boolean;
  isPaperConsentRequired?: boolean;
  defaultEmailId?: string;
  addSignatureId?: string;
  defaultAddSignatureId?: string;
  viewUpdateId?: string;
  defaultVerbalConsentId?: string;
  verbalConsentId?: string;
  defaultPaperConsentId?: string;
  paperConsentId?: string;
  defaultViewUpdateId?: string;
  defaultParentGuardianNoId?: string;
  parentGuardianNoId?: string;
  parentSignatureId?: string;
  defaultParentSignatureId?: string;
  verbalConsentValue?: { verbal: boolean }[];
  paperConsentValue?: { paperConsent?: boolean }[];
  onSaveClick?: (sign: string, id: number) => void;
  onConsentSignedDateChange?: (
    consentSignedDate: string | null,
    index: number,
  ) => void;
  onVerbalConsentClick?: (value: boolean, index: number) => void;
  onPaperConsentClick?: (value: boolean, index: number) => void;
  staffSignature?: boolean;
  nameLabelId?: string;
  nameDefaultLabelId?: string;
  licenseLabelId?: string;
  licenseDefaultLabelId?: string;
  revokeChecked?: boolean;
  hasEditPermission?: boolean;
  minDate?: dayjs.Dayjs;
};

const ConsentSignature = ({
  signatureData,
  addSignatureId,
  nameLabelId,
  nameDefaultLabelId,
  licenseLabelId,
  licenseDefaultLabelId,
  defaultAddSignatureId,
  defaultParentGuardianNoId,
  parentGuardianNoId,
  defaultEmailId,
  defaultRelationshipWithChildId,
  defaultViewUpdateId,
  defaultVerbalConsentId,
  defaultPaperConsentId,
  defaultParentSignatureId,
  emailId,
  isDateRequired,
  isVerbalConsentRequired,
  isPaperConsentRequired,
  relationshipWithChildId,
  viewUpdateId,
  verbalConsentId,
  paperConsentId,
  parentSignatureId,
  onSaveClick,
  verbalConsentValue,
  onConsentSignedDateChange,
  onVerbalConsentClick,
  paperConsentValue,
  onPaperConsentClick,
  staffSignature,
  revokeChecked,
  hasEditPermission,
  minDate,
}: ConsentSignatureType) => {
  const [signClicked, setSignClicked] = useState<boolean>(false);

  const [signature, setSignature] = useState<ConsentClientSignature[]>([
    {
      id: '',
      name: '',
      relationship: '',
      email: '',
      signature: '',
      date: '',
      consentSignedDate: null,
      license: '',
      rowKey: '',
      partitionKey: '',
      isLegal: false,
      isPrimary: false,
      isGuardian: false,
      verbal: false,
      paperConsent: false,
    },
  ]);
  const [clickedIndex, setClickedIndex] = useState<number>(0);
  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const { lookups } = useContext(LookUpContext);

  useEffect(() => {
    setSignature(signatureData);
  }, [signatureData]);

  const signatureCallBack = (response: SignatureStateType[], index: number) => {
    setClickedIndex(index);
    const updatedSignatures: ConsentClientSignature[] = [];
    signatureData.forEach(
      (sign: ConsentClientSignature, signatureIndex: number) => {
        if (clickedIndex === signatureIndex) {
          const signatureResponseData: ConsentClientSignature = {
            ...sign,
            signature: response[0].signaturedata,
            date: signatureData[signatureIndex].date ?? null,
            partitionKey: sign.partitionKey,
            rowKey: sign.rowKey,
            isLegal: sign.isLegal,
            isPrimary: sign.isPrimary,
            isGuardian: sign.isGuardian,
          };
          updatedSignatures.push(signatureResponseData);
        } else {
          const signatureResponseData: ConsentClientSignature = {
            ...sign,
            signature: sign.signature,
            date: sign.date,
            partitionKey: sign.partitionKey,
            rowKey: sign.rowKey,
            isLegal: sign.isLegal,
            isPrimary: sign.isPrimary,
            isGuardian: sign.isGuardian,
          };
          updatedSignatures.push(signatureResponseData);
        }
      },
    );

    setSignature(updatedSignatures);
  };

  const signatureSaveOrUPdate = (signed: string) => {
    if (onSaveClick) {
      const updatedSignatures = signature;
      updatedSignatures[clickedIndex] = {
        ...updatedSignatures[clickedIndex],
        partitionKey: '',
        rowKey: '',
        signature: signed,
      };
      onSaveClick(signed, clickedIndex);
    }
    setSignClicked(!signClicked);
  };
  const handleSuccess = (successError: AlertColor, id: string, mes: string) => {
    setOpenSnackBar(true);
    setToastrVariable(successError);
    setToastrId(id);
    setToastrDefaultMessage(mes);
  };
  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  const isDateDisabled = (name?: string): boolean => {
    if (!name) return true;
    return false;
  };

  return (
    <Box>
      <Grid container>
        <SnackBarComponent
          open={openSnackBar}
          handleClose={handleCloseSnakBar}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        {signatureData &&
          signatureData.length > 0 &&
          signatureData.slice(0, 2).map((data, index) => {
            const prefixes = [
              data.isPrimary && 'Primary',
              data.isLegal && 'Legal',
            ].filter(Boolean);

            return (
              <Grid item key={data.id}>
                {!staffSignature ? (
                  <Box className="width__ pt-md pb-md">
                    {data.name !== '' && (
                      <Box>
                        <Box className="flex__">
                          <MediumTypography
                            textColor="#008C82"
                            fontSize="14px"
                            fontweight={400}
                            labelid={parentGuardianNoId}
                            defaultlabel={defaultParentGuardianNoId}
                          />
                        </Box>
                        <Box className="flex__">
                          <MediumTypography
                            textColor="#2A4241"
                            className="pt-sm"
                            label={`${data.name}${
                              prefixes.length > 0
                                ? ` - (${prefixes.join(', ')})`
                                : ''
                            }`}
                            sxProps={{
                              whiteSpace: 'nowrap',
                            }}
                          />
                        </Box>
                        <Box className="flex__ pt-sm pb-sm">
                          <MediumTypography
                            labelid={relationshipWithChildId}
                            defaultlabel={defaultRelationshipWithChildId}
                            textColor="#97A6A5"
                          />

                          <Box>
                            <MediumTypography
                              textColor="#2A4241"
                              className="pl-xs"
                              sxProps={{
                                whiteSpace: 'nowrap',
                              }}
                              label={
                                lookups?.guardianType?.find(
                                  (g) =>
                                    g.code.toUpperCase() ===
                                    data.relationship?.toUpperCase(),
                                )?.description || '-'
                              }
                            />
                          </Box>
                          <MediumTypography
                            labelid={emailId}
                            defaultlabel={defaultEmailId}
                            textColor="#97A6A5"
                            className="pl-lg"
                          />
                          <Box>
                            <MediumTypography
                              textColor="#2A4241"
                              className="pl-xs "
                              label={
                                data.email === '' ||
                                data.email === null ||
                                data.email === undefined
                                  ? '-'
                                  : data.email
                              }
                              sxProps={{
                                whiteSpace: 'nowrap',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <Box className="flex__">
                      {(data.rowKey === '' ||
                        data.rowKey === null ||
                        data.rowKey === undefined) &&
                      (data.signature === '' ||
                        data.signature === null ||
                        data.signature === undefined) ? (
                        <ButtonComponent
                          className="btn-primary btn-cancel signature_add_Box"
                          labelId={addSignatureId}
                          defaultLabelId={defaultAddSignatureId}
                          variantType="outlined"
                          onClick={() => {
                            setClickedIndex(index);
                            setSignClicked(!signClicked);
                          }}
                          disabled={
                            isOtherLanguage() ||
                            (verbalConsentValue?.[index]?.verbal &&
                              isVerbalConsentRequired) ||
                            (paperConsentValue?.[index]?.paperConsent &&
                              isPaperConsentRequired) ||
                            data.name === ''
                          }
                        />
                      ) : (
                        <ButtonComponent
                          className="btn-primary btn-cancel signature_view_Box"
                          labelId={viewUpdateId}
                          defaultLabelId={defaultViewUpdateId}
                          variantType="outlined"
                          LeftIcon={<Editsignature />}
                          onClick={() => {
                            setClickedIndex(index);
                            setSignClicked(!signClicked);
                          }}
                          disabled={
                            isOtherLanguage() ||
                            (verbalConsentValue?.[index]?.verbal &&
                              isVerbalConsentRequired) ||
                            (isPaperConsentRequired &&
                              paperConsentValue?.[index]?.paperConsent)
                          }
                        />
                      )}
                      {isVerbalConsentRequired && (
                        <Box
                          className="ml-sm "
                          sx={{
                            alignSelf: 'center',
                          }}
                        >
                          <Box className="flex__ alignItemCenter">
                            <CheckBoxComponent
                              ischecked={
                                verbalConsentValue?.[index]?.verbal ?? false
                              }
                              CheckHandleChange={(e) => {
                                if (onVerbalConsentClick) {
                                  onVerbalConsentClick(e.target.checked, index);
                                }
                              }}
                              disabled={
                                data.name === '' ||
                                data.signature !== '' ||
                                data.rowKey !== '' ||
                                hasEditPermission
                              }
                              labelid={verbalConsentId}
                              defaultlabelid={defaultVerbalConsentId}
                            />
                          </Box>
                          <DatePickerComponent
                            className="mt-sm"
                            labelid={'AddNewSchedulerEvent.date'}
                            defaultlabelid={'Date'}
                            value={
                              data.consentSignedDate
                                ? dayjs(data.consentSignedDate)
                                : null
                            }
                            minDate={minDate}
                            handlechange={(date: Dayjs | null) => {
                              const formatedDate =
                                date === null
                                  ? null
                                  : dayjs(date).format('MM/DD/YYYY');
                              setSignature((prevData) => {
                                const newData = [...prevData];
                                newData[index].consentSignedDate = formatedDate;
                                return newData;
                              });

                              if (onConsentSignedDateChange)
                                onConsentSignedDateChange(formatedDate, index);
                            }}
                            disabledDate={
                              isDateDisabled(data.name) || hasEditPermission
                            }
                            disableFuture={minDate ? false : revokeChecked}
                            required={isDateRequired}
                          />
                          {datePickerMinDateMaxDateValidate(
                            data.consentSignedDate,
                          ) && (
                            <Box className="loginError">
                              <MediumTypography
                                labelid="datePickerMinDateMaxDateValidate"
                                defaultlabel="Enter a date between 01/01/1900 and 01/01/2100"
                                className="errorText-md"
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                      {isPaperConsentRequired && (
                        <Box
                          className="ml-sm "
                          sx={{
                            alignSelf: 'center',
                          }}
                        >
                          <Box className="flex__ alignItemCenter">
                            <CheckBoxComponent
                              ischecked={
                                paperConsentValue?.[index]?.paperConsent ??
                                false
                              }
                              CheckHandleChange={(e) => {
                                if (onPaperConsentClick) {
                                  onPaperConsentClick(e.target.checked, index);
                                }
                              }}
                              disabled={
                                data.name === '' ||
                                data.signature !== '' ||
                                data.rowKey !== '' ||
                                hasEditPermission
                              }
                              labelid={paperConsentId}
                              defaultlabelid={defaultPaperConsentId}
                            />
                          </Box>
                          <DatePickerComponent
                            className="mt-sm"
                            labelid={'AddNewSchedulerEvent.date'}
                            defaultlabelid={'Date'}
                            value={
                              data.consentSignedDate
                                ? dayjs(data.consentSignedDate)
                                : null
                            }
                            minDate={minDate}
                            handlechange={(date: Dayjs | null) => {
                              const formatedDate =
                                date === null
                                  ? null
                                  : dayjs(date).format('MM/DD/YYYY');
                              setSignature((prevData) => {
                                const newData = [...prevData];
                                newData[index].consentSignedDate = formatedDate;
                                return newData;
                              });

                              if (onConsentSignedDateChange)
                                onConsentSignedDateChange(formatedDate, index);
                            }}
                            disabledDate={
                              isDateDisabled(data.name) || hasEditPermission
                            }
                            disableFuture={minDate ? false : revokeChecked}
                            required={isDateRequired}
                          />
                          {datePickerMinDateMaxDateValidate(
                            data.consentSignedDate,
                          ) && (
                            <Box className="loginError">
                              <MediumTypography
                                labelid="datePickerMinDateMaxDateValidate"
                                defaultlabel="Enter a date between 01/01/1900 and 01/01/2100"
                                className="errorText-md"
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box className="width__100 pt-xs pb-lg">
                    {(data.rowKey === '' ||
                      data.rowKey === null ||
                      data.rowKey === undefined) &&
                    (data.signature === '' ||
                      data.signature === null ||
                      data.signature === undefined) ? (
                      <ButtonComponent
                        className="btn-primary btn-cancel signature_add_Box"
                        labelId={addSignatureId}
                        defaultLabelId={defaultAddSignatureId}
                        variantType="outlined"
                        onClick={() => {
                          setClickedIndex(index);
                          setSignClicked(!signClicked);
                        }}
                        disabled={isOtherLanguage()}
                      />
                    ) : (
                      <ButtonComponent
                        className="btn-primary btn-cancel signature_view_Box"
                        labelId={viewUpdateId}
                        defaultLabelId={defaultViewUpdateId}
                        variantType="outlined"
                        LeftIcon={<Editsignature />}
                        onClick={() => {
                          setClickedIndex(index);
                          setSignClicked(!signClicked);
                        }}
                        disabled={isOtherLanguage()}
                      />
                    )}
                    <Box className="flex__ pt-lg">
                      <MediumTypography
                        labelid={nameLabelId}
                        defaultlabel={nameDefaultLabelId}
                        textColor="#97A6A5"
                      />

                      <Box className="width100px">
                        <Tooltip
                          title={
                            data.name !== undefined &&
                            data.name !== null &&
                            data.name.length > 10
                              ? data.name
                              : ''
                          }
                          followCursor
                          arrow={true}
                        >
                          <Box>
                            <MediumTypography
                              textColor="#2A4241"
                              className="pl-xs ellipsis"
                              label={
                                data.firstName === null ||
                                data.firstName === '' ||
                                data.firstName === undefined
                                  ? '-'
                                  : data.lastName + ', ' + data.firstName
                              }
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      <MediumTypography
                        labelid={licenseLabelId}
                        defaultlabel={licenseDefaultLabelId}
                        textColor="#97A6A5"
                        className="pl-sm"
                      />
                      <Box className="width200px">
                        <Tooltip
                          title={
                            data.license !== null &&
                            data.license &&
                            data.license.length > 10
                              ? data.license
                              : ''
                          }
                          followCursor
                          arrow={true}
                        >
                          <Box>
                            <MediumTypography
                              textColor="#2A4241"
                              className="pl-xs "
                              label={
                                data.license === null ||
                                data.license === '' ||
                                data.license === undefined
                                  ? '-'
                                  : data.license
                              }
                              sxProps={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Grid>
            );
          })}

        {signatureData && signatureData.length > 0 && signClicked && (
          <Box component="div">
            <SignatureComponent
              onCancelClick={() => {
                setSignClicked(false);
              }}
              signatureRowKey={signatureData[clickedIndex].rowKey ?? ''}
              signaturePartitionKey={
                signatureData[clickedIndex].partitionKey ?? ''
              }
              signatureSaveOrUPdate={signatureSaveOrUPdate}
              handleSuccess={handleSuccess}
              type={staffSignature ? 'Employee' : 'others'}
              signatureCallBack={(sign: SignatureStateType[]) => {
                signatureCallBack(sign, clickedIndex);
              }}
              signature={signature[clickedIndex].signature ?? ''}
              signClicked={signClicked}
              labelId={parentSignatureId}
              defaultLabelId={defaultParentSignatureId}
              resetCallBackForTypeEmployee={() => {
                setSignClicked(false);
                const updateSign = signature;
                updateSign[clickedIndex] = {
                  ...updateSign[clickedIndex],
                  signature: '',
                  partitionKey: '',
                  rowKey: '',
                };
              }}
              hideRemoveButton={true}
              hideResetButton={hasEditPermission}
            />
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default ConsentSignature;
